<template lang="pug">
  v-app(:style="cssProps")
    v-main(:class="displayClass")
      .messages
        v-snackbar.elevation-0(top flat :timeout="snackbarProp.timeout" :color="snackbarProp.color" v-model="snackbarProp.show" :multi-line="snackbarProp.text2 != ''")
          .text-md.font-medium.basic100--text {{ snackbarProp.text }}
          .text-sm.basic100--text(v-if="snackbarProp.text2") {{ snackbarProp.text2 }}
          template(#action)
            v-btn.mr-1(@click="snackbarProp.show = false" icon small)
              eva-icon.d-flex(name="close" fill="#ffffff66" height="16px")
        upgrade-package(v-if="upgradeDialog" :open.sync="upgradeDialog")
        upgrade-free(v-if="upgradeFree" :open.sync="upgradeFree")

      router-view
</template>

<script>
import Vue from 'vue'
import { mapState, mapGetters, mapActions } from 'vuex'
import Cookies from 'js-cookie'
import Fingerprint2 from 'fingerprintjs2'
import UpgradePackage from '@/views/admin/billing/UpgradePackage.vue'
import UpgradeFree from '@/views/admin/billing/UpgradeFree.vue'
import { setupLanguage } from '@/utils'

export default {
  name: 'App',
  components: { UpgradePackage, UpgradeFree },
  data: () => ({
    upgradeDialog: false,
    upgradeFree: false,
    snackbarProp: {
      show: false,
      color: 'success',
      text: '',
      text2: '',
      timeout: 3000
    }
  }),
  computed: {
    ...mapState('user', ['user', 'member']),
    ...mapGetters({
      message: 'getMessage',
      tourSteps: 'tours/getSteps',
      currency: 'getCompanyCurrency',
      lang: 'language/getLanguage',
      displaySize: 'ui/getDisplaySize'
    }),
    cssProps () {
      const themeColors = {}
      Object.keys(this.$vuetify.theme.themes[this.$theme]).forEach((color) => {
        themeColors[`--v-${color}`] = this.$vuetify.theme.themes[this.$theme][color]
      })
      return themeColors
    },
    displayClass () {
      switch (this.displaySize) {
        case 'xx-small':
          return 'zoom-67'
        case 'x-small':
          return 'zoom-75'
        case 'small':
          return 'zoom-80'
        case 'medium':
          return 'zoom-90'
        case 'large':
          return 'zoom-100'
        case 'x-large':
          return 'zoom-110'
        default:
          return 'zoom-100'
      }
    }
  },
  watch: {
    message (val) {
      if (val.type === 'success') {
        this.showSnackbarSuccess(val)
      } else if (val.type === 'error') {
        this.showSnackbarError(val)
      } else if (val.type === 'info') {
        this.showSnackbarInfo(val)
      } else if (val.type === 'warning') {
        this.showSnackbarWarning(val)
      } else if (val.type === 'upgrade') {
        this.upgradeDialog = true
      } else if (val.type === 'free2paid') {
        this.upgradeFree = true
      } else {
        this.showSnackbarCustom(val)
      }
    },
    lang (val) {
      this.$root.$i18n.locale = val
    }
  },
  mounted () {
    Vue.prototype.$currency = this.currency

    // Language
    setupLanguage()
    this.$store.commit('language/recoverLanguage')

    // User
    let user = null
    let cookieUser = Cookies.get('user')
    if (cookieUser) {
      user = JSON.parse(cookieUser)
    }
    this.$store.commit('user/setUser', user)

    let device = null
    let cookieDevice = Cookies.get('yz_device')
    if (cookieDevice) {
      device = JSON.parse(cookieDevice)
      Vue.prototype.$isMobile = true
      this.disablePageZoom()
      this.syncUserWithDevice(user, device)
    } else {
      Vue.prototype.$isMobile = false
    }
    this.$store.commit('mobileapp/setDevice', device)

    // Affiliate
    const afId = this.$route.query.af_id
    if (afId) {
      const currentAfId = Cookies.get('af_id')
      if (currentAfId != afId) {
        Cookies.set('af_id', afId, { expires: 30 })
      }
      let query = {...this.$route.query}
      delete query.af_id
      this.$router.replace({ query })
    }
    const afIdCookie = Cookies.get('af_id')
    this.$store.commit('ui/setAffiliateId', afIdCookie)

    // Reminder
    let reminder = null
    let storageReminder = localStorage.getItem('reminder')
    if (storageReminder) {
      reminder = JSON.parse(storageReminder)
    }
    this.$store.commit('reminder/setReminder', reminder)
    this.$store.commit('ui/setLastIncomeTabPosition', localStorage.getItem('yz_last_income_tab_position') | 0)

    // Topbar
    const items = JSON.parse(localStorage.getItem('yz_top_bar_items'))
    if (items) {
      this.$store.commit('ui/setCurrentTopBarItems', items)
    }

    this.storeDeviceId()
    this.updateOpenTracker()

    // posthog
    setTimeout(() => {
      if (this.member && window.posthog) {
        window.posthog.identify(
          this.member.id,
          {
            name: this.member.user.first_name + ' ' + this.member.user.last_name,
            company: this.member.company.name,
            slug: this.member.company.slug,
            email: this.member.user.email,
            phone: this.member.phone,
            role: this.member.role
          }
        )
      }
    }, 500)

    // Global function for in-app browser navigation used in mobile app
    window.pushURL = path => {
      this.$router.push(path)
    }

    this.loadColors()

    // Consultation Height
    this.$store.commit('ui/setInitDisplayConsultationHeightAdjustment')

    // remove scroll on number input
    document.addEventListener('wheel', this.handleWheel, { passive: false });
  },
  methods: {
    ...mapActions([
      'showMessage',
      'updateCompany'
    ]),
    showSnackbarError (val) {
      this.snackbarProp.text = val.text
      this.snackbarProp.text2 = val.text2
      this.snackbarProp.color = 'danger'
      this.snackbarProp.show = true
    },
    showSnackbarSuccess (val) {
      this.snackbarProp.text = val.text
      this.snackbarProp.text2 = val.text2
      this.snackbarProp.color = 'success'
      this.snackbarProp.show = true
    },
    showSnackbarInfo (val) {
      this.snackbarProp.text = val.text
      this.snackbarProp.text2 = val.text2
      this.snackbarProp.color = 'info'
      this.snackbarProp.show = true
    },
    showSnackbarWarning (val) {
      this.snackbarProp.text = val.text
      this.snackbarProp.text2 = val.text2
      this.snackbarProp.color = 'warning'
      this.snackbarProp.show = true
    },
    showSnackbarCustom (val) {
      this.snackbarProp.text = val.text
      this.snackbarProp.text2 = val.text2
      this.snackbarProp.color = val.type
      this.snackbarProp.show = true
    },
    storeDeviceId () {
      let handleCallback = (components) => {
        var values = components.map(function (component) { return component.value })
        var murmur = Fingerprint2.x64hash128(values.join(''), 31)
        this.$store.commit('user/setDeviceId', murmur)

        // Analytics
        if (this.user) {
          this.$logEvent('user_session.started')
        }
      }
      if (window.requestIdleCallback) {
        requestIdleCallback(function () {
          Fingerprint2.get(function (components) {
            handleCallback(components)
          })
        })
      } else {
        setTimeout(function () {
          Fingerprint2.get(function (components) {
            handleCallback(components)
          })
        }, 500)
      }
    },
    syncUserWithDevice (user, device) {
      const data = {
        uuid: device.uuid
      }
      if (user) {
        data.user = user.id
        data.logged_in = true
      } else {
        data.logged_in = false
      }
      this.$axios.post('/notifications/update_device/', data).catch(() => {})
    },
    disablePageZoom () {
      const tag = document.createElement('meta')
      tag.setAttribute('name','viewport')
      tag.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0')
      document.head.appendChild(tag)
    },
    updateOpenTracker () {
      setTimeout( () => {
        if (this.$route.query.t) {
          this.$axios.get(`/t/${this.$route.query.t}/`)
        }
      }, 1000)
    },
    loadColors () {
      Vue.prototype.$colors = this.$vuetify.theme.themes.light
    },
    handleWheel(event) {
      const element = document.activeElement;
      if (element && (element.type === 'number' || element.classList.contains('v-text-field__input'))) {
        event.preventDefault();
        element.blur();
      }
    }
  },
  beforeDestroy() {
    document.removeEventListener('wheel', this.handleWheel);
  },
}
</script>

<style lang="scss">
@font-face {
  font-family: 'GothamRounded';
  src: url('./assets/fonts/GothamRounded-Medium.otf') format('opentype');
  font-weight: 500;
}
.v-snack__wrapper {
  max-width: 600px !important;
  width: 100% !important;
  text-align: center !important;

}
.v-snack__content {
  display: block !important;
}
.prevent-text-selection {
  -webkit-user-select: none;
  -moz-user-select: -moz-none;
  -ms-user-select: none;
  user-select: none;
}
.clickable {
  cursor: pointer;
}
.text-wrapper {
  word-break: break-word;
}
.zoom-110 {
  zoom: 110%;
}
.zoom-100 {
  zoom: 100%;
}
.zoom-90 {
  zoom: 90%;
}
.zoom-80 {
  zoom: 80%;
}
.zoom-75 {
  zoom: 75%;
}
.zoom-67 {
  zoom: 67%;
}
</style>
